// Path: src/components/dashboard/HomeLayout.tsx
// Optimized to reduce excessive renders by memoizing dependencies
// Adjusted sun size to 48px desktop, 44px mobile, 14px on /home/profile
// Fixed integration with Partners.tsx to prevent rendering issues
// Optimized to further reduce excessive renders by stabilizing useOlsmeTimer
// Memoized props passed to Partners.tsx to prevent unnecessary re-renders
// 12:00pm 05/24/25
// Added debouncing to useOlsmeTimer to reduce excessive renders
// Ensured consistent selectedPartnerValue with Partners.tsx
// Updated selectedPartnerValue to default to "w0-1" (OLS Partners)
// Added debouncing to useOlsmeTimer to reduce excessive renders
// Ensured consistent selectedPartnerValue with Partners.tsx
// Repositioned Partners component above social follow links and removed sticky behavior
// Path: src/components/dashboard/HomeLayout.tsx
// Updated to integrate TimerBar component for 300-second session timer with 100-second intervals
// Replaced useOlsmeTimer with useTimer from TimerContext.tsx for consistency
// Enhanced SEO with Open Graph, Twitter Card meta tags, and lead generation CTA
// Added ARIA labels to social links for accessibility
// Preserved memoization, debouncing, sun size adjustments, and Partners integration
// 05/26/25 12:59 PM +07
// Path: src/components/dashboard/HomeLayout.tsx
// Updated to align structure with PublicLayout.tsx for authenticated users
// Integrated TimerBar for 300-second session timer with 100-second intervals
// Added user-specific navigation, status display, and logout functionality
// Enhanced SEO with Open Graph, Twitter Card meta tags, and lead generation CTA
// Ensured single footer rendering, memoization, debouncing, and accessibility
// 05/26/25 01:03 PM +07
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Button, Typography, Layout, Menu } from "antd";
import { useNavigate, Outlet, Link, useLocation } from "react-router-dom";
import { useUserStore } from "../../stores/UserStore";
import { useFirebase } from "../dev/Firebase/Firebase";
import Footer from "../dev/utils/Footer/Footer";
import LoginMonitor from "../auth/components/LoginMonitor";
import Partners from "../auth/components/Partners";
import MiniOLSUpdates from "../auth/components/MiniOLSUpdates";
import {
  SiGithub,
  SiBlogger,
  SiFacebook,
  SiLinkedin,
  SiYoutube,
  SiX,
} from "@icons-pack/react-simple-icons";
import styled from "styled-components";
import { UserOutlined, DownOutlined, MenuOutlined, CloseOutlined } from "@ant-design/icons";
import OLSCalendar from "../auth/components/OLSCalendar";
import MiniLightTimeToggle from "../aols/aeo/OLSCalendarTime/MiniLightTimeToggle";
import { useTimer } from "../../context/TimerContext";
import TimerBar from "../TimerBar";
import MenuLogin from "../auth/components/MenuLogin";
import { UserRole, UserRoleType } from "../../constants/UserEnums";
import "../auth/login.css";

const { Title } = Typography;
const { Sider, Content } = Layout;

// Wrapper for the entire layout
const LayoutWrapper = styled.div`
  position: relative;
  min-height: 100vh;
  overflow-x: hidden !important;
  overflow: visible !important;
  width: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  box-sizing: border-box;
  font-size: 16px;
  display: flex;
  flex-direction: column;
  border: none !important;
  box-shadow: none !important;
  outline: none !important;

  .home-layout {
    animation: fadeIn 1.5s ease-in;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @media (max-width: 768px) {
    .ant-layout,
    .ant-btn {
      font-size: 14px !important;
    }
  }
`;

// Styled button for mobile menu toggle
const MobileMenuButton = styled(Button)`
  position: fixed;
  top: 10px;
  left: 10px;
  z-index: 1010;
  background: #ffcc00;
  border: none;
  color: #0427f4;
  font-size: 18px;
  transition: background 0.3s ease, transform 0.3s ease;
  &:hover {
    background: #ffaa00;
    color: #0427f4;
    transform: scale(1.05);
  }
  border: none !important;
  outline: none !important;
`;

// Container for the sidebar
const SiderContainer = styled.div<{ $collapsed: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 100;
  height: 100vh;
  width: ${(props) => (props.$collapsed ? "0" : "200px")};
  overflow: hidden;
  transition: width 0.3s ease;
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
`;

// Sidebar styling
const StyledSider = styled.div<{ $collapsed: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  height: auto;
  max-height: 100vh;
  background: linear-gradient(to bottom, #fff5e6, #fff);
  transition: transform 0.3s ease;
  overflow-y: auto;
  padding-top: 60px;
  width: 200px;
  transform: ${(props) => (props.$collapsed ? "translateX(-200px)" : "translateX(0)")};
  box-shadow: ${(props) => (props.$collapsed ? "none" : "2px 0 10px rgba(0, 0, 0, 0.5)")};
  @media (max-width: 768px) {
    width: 150px;
    padding-top: 50px;
  }
  border: none !important;
  outline: none !important;
`;

// Content area styling
const ContentArea = styled(Content)`
  display: flex;
  flex-direction: column;
  padding: 0 !important;
  overflow: auto;
  overflow-x: hidden !important;
  width: 100% !important;
  max-width: 100vw !important;
  margin: 0 !important;
  position: relative;
  align-items: flex-start;
  padding-top: 60px;
  flex: 1;
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
  @media (max-width: 768px) {
    padding: 0 !important;
    padding-top: 40px;
  }
`;

// Main content styling
const MainContent = styled.div`
  padding: 50px;
  text-align: center;
  flex: 1;
  width: 100%;
  @media (max-width: 768px) {
    padding: 30px;
  }
`;

// Sun container styling
const SunContainer = styled.div<{ $isProfilePage: boolean }>`
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%) ${({ $isProfilePage }) => ($isProfilePage ? "scale(0.29)" : "scale(1)")};
  width: 48px;
  padding-bottom: 12px;
  z-index: 5000;
  background: transparent;

  .sun {
    width: 48px !important;
    height: 48px !important;
    position: relative;
  }

  .sun .center {
    width: 28px !important;
    height: 28px !important;
    top: 10px !important;
    left: 10px !important;
  }

  .sun .ray {
    width: 6px !important;
    height: 14px !important;
  }

  @media (max-width: 768px) {
    padding: 6px;
    width: 44px;

    .sun {
      width: 44px !important;
      height: 44px !important;
    }

    .sun .center {
      width: 26px !important;
      height: 26px !important;
      top: 9px !important;
      left: 9px !important;
    }

    .sun .ray {
      width: 5px !important;
      height: 13px !important;
    }
  }
`;

// Container for top-right elements
const TopRightContainer = styled.div`
  position: fixed;
  top: 40px;
  right: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  z-index: 900;
  max-width: calc(100vw - 40px);
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
  @media (max-width: 768px) {
    top: 30px;
    right: 10px;
    gap: 4px;
    max-width: calc(100vw - 20px);
  }
`;

// Toggle menu button styling
const ToggleMenuButton = styled(Button)`
  background: #ffcc00;
  border: none;
  color: #0427f4;
  font-size: 18px;
  transition: background 0.3s ease, transform 0.3s ease;
  &:hover {
    background: #ffaa00;
    color: #0427f4;
    transform: scale(1.05);
  }
  border: none !important;
  outline: none !important;
`;

// Right menu styling
const RightMenu = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 60px;
  right: 0;
  width: 200px;
  height: auto;
  max-height: calc(100vh - 60px);
  background: linear-gradient(to bottom, #fff5e6, #fff);
  transition: transform 0.3s ease;
  transform: ${(props) => (props.$isOpen ? "translateX(0)" : "translateX(200px)")};
  box-shadow: ${(props) => (props.$isOpen ? "-2px 0 10px rgba(0, 0, 0, 0.5)" : "none")};
  overflow-y: auto;
  padding: 10px 0;
  z-index: 999;
  display: flex;
  flex-direction: column;
  @media (max-width: 768px) {
    width: 150px;
  }
  border: none !important;
  outline: none !important;
`;

// Status container styling
const StatusContainer = styled.div`
  margin: 10px;
  color: #0427f4;
  text-align: right;
`;

// Status text styling
const StatusText = styled.span`
  display: block;
`;

// Close menu button styling
const CloseMenuButton = styled(Button)`
  background: #ffcc00;
  border: none;
  color: #0427f4;
  font-size: 16px;
  transition: background 0.3s ease, transform 0.3s ease;
  margin: 10px;
  &:hover {
    background: #ffaa00;
    color: #0427f4;
    transform: scale(1.05);
  }
  border: none !important;
  outline: none !important;
`;

// Wrapper for calendar link
const CalendarLinkWrapper = styled(Link)`
  display: inline-block;
  &:hover {
    text-decoration: none;
  }
  border: none !important;
  outline: none !important;
`;

// Wrapper for light time toggle link
const LightTimeLinkWrapper = styled(Link)`
  display: inline-block;
  text-decoration: none;
  &:hover {
    text-decoration: none;
  }
  border: none !important;
  outline: none !important;
`;

// Wrapper for timer link
const TimerLinkWrapper = styled(Link)`
  display: inline-block;
  text-decoration: none;
  &:hover {
    text-decoration: none;
  }
  border: none !important;
  outline: none !important;
`;

// Timer display styling with blinking effect when active
const TimerDisplay = styled.div<{ $isSessionActive: boolean }>`
  display: flex;
  align-items: center;
  background: rgba(97, 13, 127, 0.9);
  padding: 5px 10px;
  border: 2px solid #0427f4;
  border-radius: 8px;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.2);
  width: auto;
  height: auto;
  position: relative;
  @media (max-width: 768px) {
    padding: 3px 8px;
  }

  ${(props) =>
    props.$isSessionActive &&
    `
      @keyframes blink {
        0% { opacity: 1; }
        50% { opacity: 0.5; }
        100% { opacity: 1; }
      }

      &::after {
        content: '';
        position: absolute;
        top: -8px;
        left: -8px;
        right: -8px;
        bottom: -8px;
        border: 4px solid #00ff00;
        border-radius: 12px;
        animation: blink 1s infinite;
        z-index: -1; /* Ensure border is behind the content */
      }
    `}
  border: none !important;
  outline: none !important;
`;

// Timer text styling
const TimerText = styled.span`
  color: rgb(251, 251, 249);
  font-size: 14px;
  font-weight: bold;
  white-space: nowrap;
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

// Partners wrapper styling
const PartnersWrapper = styled.div`
  margin: 20px auto;
  text-align: center;
  width: 100%;
`;

// Social icon wrapper styling
const SocialIconWrapper = styled.a`
  margin: 0 10px;
  display: inline-block;
`;

interface User {
  userRole?: UserRoleType;
  verified?: string | boolean;
  submittedForVerification?: boolean;
}

interface UserStore {
  user: User | null;
  userRole: UserRoleType;
  verified: string | boolean;
  clearUser: () => void;
}

const HomeLayout: React.FC = ({ children }) => {
  const { auth } = useFirebase();
  const [selectedPartnerValue, setSelectedPartnerValue] = useState<string>("w0-1"); // Default to "OLS Partners"
  const [isEstateOpen, setIsEstateOpen] = useState<boolean>(false);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isLeftMenuOpen, setIsLeftMenuOpen] = useState<boolean>(false);
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [debouncedTimer, setDebouncedTimer] = useState<string>("");
  const [debouncedSessionActive, setDebouncedSessionActive] = useState<boolean>(false);
  const navigate = useNavigate();
  const userStore = useUserStore() as UserStore;
  const { timerData } = useTimer();
  const { timer, isSessionActive, sessionProgress, intervalLabel } = timerData;
  const location = useLocation();
  const hasRenderedFooter = useRef(false);
  const prevTimerRef = useRef(timer);
  const renderCount = useRef(0);
  const isMounted = useRef(false);

  const isProfilePage = location.pathname === "/home/profile";
  const isMobile = dimensions.width < 768;

  // Debounce timer updates to reduce re-renders
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedTimer(timer);
      setDebouncedSessionActive(isSessionActive);
    }, 1000); // Update every second
    return () => clearTimeout(handler);
  }, [timer, isSessionActive]);

  const updateWindowDimensions = useCallback(() => {
    setDimensions({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  }, []);

  useEffect(() => {
    updateWindowDimensions();
    window.addEventListener("resize", updateWindowDimensions);
    return () => window.removeEventListener("resize", updateWindowDimensions);
  }, [updateWindowDimensions]);

  const handleLogout = useCallback(async () => {
    try {
      await auth.signOut();
      userStore.clearUser();
      setIsMenuOpen(false);
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  }, [auth, userStore, navigate]);

  const onMenuClick = useCallback(({ key }: { key: string }) => {
    if (key === "logout") {
      handleLogout();
    } else {
      setIsMenuOpen(false);
    }
  }, [handleLogout]);

  const userMenuItems = useMemo(() => [
    {
      key: "profile",
      label: "Profile",
      onClick: () => navigate("/home/profile"),
    },
    {
      key: "referral",
      label: "Referral",
      onClick: () => navigate("/home/referral"),
    },
    {
      key: "evaluation",
      label: "Evaluation",
      onClick: () => navigate("/home/evaluationS"),
    },
    {
      key: "intro",
      label: "Intro",
      onClick: () => navigate("/home/intro"),
    },
  ], [navigate]);

  const authMenuItems = useMemo(() => [
    {
      key: "Initi",
      label: "OLS Initi",
      link: "/initi",
    },
    {
      key: "assets",
      label: "OLS Assets",
      link: "/home/assets",
    },
    {
      key: "support",
      label: "Support",
      link: "/home/support",
    },
  ], []);

  const combinedMenuItems = useMemo(() => [
    ...userMenuItems.map((item) => ({
      key: item.key,
      label: item.link ? <Link to={item.link}>{item.label}</Link> : item.label,
      onClick: item.onClick,
    })),
    ...authMenuItems.map((item) => ({
      key: item.key,
      label: item.link ? <Link to={item.link}>{item.label}</Link> : item.label,
      onClick: item.onClick || (() => setIsMenuOpen(false)),
    })),
    {
      key: "logout",
      label: "Logout",
      onClick: handleLogout,
    },
  ], [userMenuItems, authMenuItems, handleLogout]);

  const toggleEstate = useCallback(() => setIsEstateOpen((prev) => !prev), []);

  const toggleMenu = useCallback(() => setIsMenuOpen((prev) => !prev), []);

  const toggleLeftMenu = useCallback(() => setIsLeftMenuOpen((prev) => !prev), []);

  const statusText = userStore.verified
    ? "Verified"
    : userStore.user?.submittedForVerification
    ? "Pending"
    : "Not Verified";

  const accountTypeText = useCallback(() => {
    if (!userStore.user) return "Unknown";
    switch (userStore.userRole) {
      case UserRole.OLSS_COL:
        return "uOLSS";
      case UserRole.OLSLM_COL:
        return "uOLSLM";
      case UserRole.OLSME_COL:
        return "uOLSME";
      default:
        return userStore.userRole || "Unknown";
    }
  }, [userStore.user, userStore.userRole]);

  const partnersProps = useMemo(() => ({
    state: { selectedPartnerValue, isEstateOpen },
    onPartnersClick: toggleEstate,
    onChangePartners: setSelectedPartnerValue,
  }), [selectedPartnerValue, isEstateOpen, toggleEstate]);

  useEffect(() => {
    if (isMounted.current) {
      renderCount.current += 1;
      console.log('HomeLayout: Render count:', renderCount.current, 'for path:', location.pathname);

      setIsLeftMenuOpen(false);
      hasRenderedFooter.current = false;
      console.log("Route changed to:", location.pathname, "Menu collapsed by default");
      console.log("Rendering single footer in HomeLayout for path:", location.pathname);
    } else {
      isMounted.current = true;
    }

    return () => {
      console.log("HomeLayout: Cleanup for path:", location.pathname);
      hasRenderedFooter.current = false;
    };
  }, [location.pathname]);

  useEffect(() => {
    prevTimerRef.current = timer;
  }, [timer]);

  console.log("HomeLayout rendering");

  return (
    <LayoutWrapper className="home-layout">
      <Helmet>
        <title>Dashboard - OneLightSystem OLS</title>
        <meta
          name="description"
          content="Access your OLS dashboard for Sun Light Meditation education and Light Civilization. Join our radiant community at olsme.com/OLSStudentProspect!"
        />
        <meta
          name="keywords"
          content="OLS dashboard, Sun Light Meditation, user profile, referral program, meditation evaluation, OneLightSystem OLS, personal growth, join OLS"
        />
        <meta name="robots" content="noindex, nofollow" /> {/* Authenticated pages typically aren't indexed */}
        <meta property="og:title" content="OLS Dashboard | Sun Light Meditation" />
        <meta
          property="og:description"
          content="Manage your Sun Light Meditation journey with OLS. Explore profiles, referrals, and evaluations at olsme.com/OLSStudentProspect."
        />
        <meta property="og:url" content="https://www.olsme.com/home" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.olsme.com/logo.jpg" />
        <meta property="og:image:alt" content="OneLightSystem OLS Logo" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="OLS Dashboard | Sun Light Meditation" />
        <meta
          name="twitter:description"
          content="Join OLS to manage your Sun Light Meditation journey. Register at olsme.com/OLSStudentProspect."
        />
        <meta name="twitter:image" content="https://www.olsme.com/logo.jpg" />
        <meta name="twitter:image:alt" content="OneLightSystem OLS Logo" />
        <meta name="twitter:site" content="@onelightsystem" />
        <meta name="twitter:creator" content="@onelightsystem" />
        <link rel="canonical" href="https://www.olsme.com/home" />
      </Helmet>
      <Layout style={{ minHeight: "100vh" }}>
        <SiderContainer $collapsed={!isLeftMenuOpen}>
          <StyledSider $collapsed={!isLeftMenuOpen}>
            <MenuLogin toggleCollapsed={toggleLeftMenu} collapsed={!isLeftMenuOpen} />
          </StyledSider>
        </SiderContainer>

        <ContentArea>
          <TopRightContainer>
            <CalendarLinkWrapper to="/OLScalendarTime" aria-label="View OLS Calendar Time">
              <Tooltip title="View OLS Calendar Time">
                <OLSCalendar />
              </Tooltip>
            </CalendarLinkWrapper>
            <LightTimeLinkWrapper to="/OLScalendarTime" aria-label="View Light Time">
              <Tooltip title="View Light Time">
                <MiniLightTimeToggle />
              </Tooltip>
            </LightTimeLinkWrapper>
            <TimerLinkWrapper to="/olsme" aria-label="View Timer">
              <TimerDisplay $isSessionActive={debouncedSessionActive}>
                <TimerText>{debouncedTimer}</TimerText>
              </TimerDisplay>
            </TimerLinkWrapper>
            <ToggleMenuButton
              icon={<MenuOutlined />}
              onClick={toggleMenu}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            />
            <RightMenu $isOpen={isMenuOpen}>
              <StatusContainer className="status-container">
                <StatusText>Status: {statusText}</StatusText>
                <StatusText>Account Type: {accountTypeText()}</StatusText>
              </StatusContainer>
              <Menu
                theme="light"
                mode="vertical"
                onClick={onMenuClick}
                items={combinedMenuItems}
              />
              <CloseMenuButton
                icon={<CloseOutlined />}
                onClick={toggleMenu}
                aria-label="Close menu"
              />
            </RightMenu>
          </TopRightContainer>

          <MobileMenuButton
            icon={isLeftMenuOpen ? <CloseOutlined /> : <MenuOutlined />}
            onClick={toggleLeftMenu}
            aria-label={isLeftMenuOpen ? "Close menu" : "Open menu"}
          />

          <SunContainer $isProfilePage={isProfilePage} className={isMobile ? "is-mobile" : ""}>
            <div className="sun">
              <div className="center"></div>
              <div className="ray r-1"></div>
              <div className="ray r-2"></div>
              <div className="ray r-3"></div>
              <div className="ray r-4"></div>
              <div className="ray r-5"></div>
              <div className="ray r-6"></div>
              <div className="ray r-7"></div>
              <div className="ray r-8"></div>
              <div style={{ width: "48px", height: "48px", position: "static", visibility: "hidden" }}></div>
            </div>
          </SunContainer>

          <MainContent>
            <TimerBar
              progress={sessionProgress}
              intervalLabel={intervalLabel}
              isSessionActive={isSessionActive}
            />
            {children || <Outlet />}
          </MainContent>

          <LoginMonitor toggleEstate={toggleEstate} state={{ isEstateOpen }} />

          <span>
            <div style={{ height: "20px" }}></div>
          </span>

          <MiniOLSUpdates />

          <span>
            <div style={{ height: "20px" }}></div>
          </span>

          <PartnersWrapper>
            <Partners {...partnersProps} />
          </PartnersWrapper>
          <div className="social-follow-wrapper">
            <SocialIconWrapper
              href="https://github.com/sponsors/onelightsystem"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit OLS GitHub Sponsors"
            >
              <SiGithub className="social-icon-image" fill="#181717" title="GitHub" />
            </SocialIconWrapper>
            <SocialIconWrapper
              href="https://www.blogger.com/profile/09343545978643567973"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit OLS Blogger Profile"
            >
              <SiBlogger className="social-icon-image" fill="#FF5722" title="Blogger" />
            </SocialIconWrapper>
            <SocialIconWrapper
              href="https://www.facebook.com/onelightsystemols"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit OLS Facebook Page"
            >
              <SiFacebook className="social-icon-image" fill="#0866FF" title="Facebook" />
            </SocialIconWrapper>
            <SocialIconWrapper
              href="https://www.linkedin.com/company/one-light-system"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit OLS LinkedIn Page"
            >
              <SiLinkedin className="social-icon-image" fill="#0A66C2" title="LinkedIn" />
            </SocialIconWrapper>
            <SocialIconWrapper
              href="https://www.youtube.com/@ONELIGHTSYSTEMOLSMeditation"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit OLS YouTube Channel"
            >
              <SiYoutube className="social-icon-image" fill="#FF0000" title="YouTube" />
            </SocialIconWrapper>
            <SocialIconWrapper
              href="https://x.com/onelightsystem"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit OLS X Profile"
            >
              <SiX className="social-icon-image" fill="#000000" title="X" />
            </SocialIconWrapper>
          </div>

          <div style={{ height: "20px" }}></div>
        </ContentArea>

        {hasRenderedFooter.current ? null : (
          <Footer
            style={{ position: "fixed", bottom: 0, width: "100%", zIndex: 1000 }}
            aria-label="Site footer"
            onRender={() => {
              hasRenderedFooter.current = true;
              console.log("Footer rendered for path:", location.pathname);
            }}
          />
        )}
      </Layout>
    </LayoutWrapper>
  );
};

export default React.memo(HomeLayout);